import {
  type Icon,
  IconArrowsDiff,
  IconBriefcase,
  IconBuildingBank,
  IconCamera,
  IconCash,
  IconDashboard,
  IconFileAi,
  IconFileCode,
  IconFileDescription,
  IconReportAnalytics,
  IconSettings,
  IconShieldDollar,
  IconTarget,
  IconToggleRight,
  IconTruckDelivery,
  IconUsers,
  IconUsersGroup,
} from '@tabler/icons-react';
import { useTheme } from 'next-themes';
import type * as React from 'react';
import { NavMain } from '@/components/nav-main';
import { NavUser } from '@/components/nav-user';
import { Separator } from '@/components/ui/separator';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { useCompanyLogo } from '@/contexts/company-logo-context';
import { useFeatureFlag } from '@/contexts/feature-flag-context';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';
import { useOpportunitiesCount } from '@/contexts/opportunities-context';
import { useSupplierDisplay } from '@/contexts/supplier-display-context';
import { isInternalAdminUser, ROUTE_ROLES } from '@/lib/authz';

export const getNavigationData = (
  user: { name?: string; email?: string; roles?: string[] } | null,
  options?: {
    opportunitiesCount?: number;
    suppliersLabel?: string;
    reconciliationEnabled?: boolean;
  }
) => {
  const {
    opportunitiesCount,
    suppliersLabel = 'Suppliers',
    reconciliationEnabled = false,
  } = options ?? {};
  type NavItem = {
    title: string;
    url: string;
    icon: Icon;
    allowedTypes?: string[];
    counter?: number | undefined;
  };

  const navMain: NavItem[] = [
    {
      title: 'Dashboard',
      url: '/dashboard',
      icon: IconDashboard,
    },
    {
      title: 'Reports',
      url: '/reports',
      icon: IconReportAnalytics,
      allowedTypes: ROUTE_ROLES.ANY_INTERNAL,
    },
    {
      title: 'Clients',
      url: '/clients',
      icon: IconBriefcase,
      allowedTypes: ROUTE_ROLES.ANY_INTERNAL,
    },
    {
      title: 'Escrow',
      url: '/escrow',
      icon: IconShieldDollar,
      allowedTypes: ROUTE_ROLES.ANY_INTERNAL,
    },
    {
      title: 'Payments',
      url: '/payments',
      icon: IconCash,
    },
    {
      title: 'Payment Files',
      url: '/payment-files',
      icon: IconFileDescription,
    },
    ...(reconciliationEnabled
      ? [
          {
            title: 'Reconciliation',
            url: '/reconciliation',
            icon: IconArrowsDiff,
            allowedTypes: ROUTE_ROLES.ANY_INTERNAL,
          },
        ]
      : []),
    {
      title: suppliersLabel,
      url: '/suppliers',
      icon: IconTruckDelivery,
    },
  ];
  if (isInternalAdminUser(user?.roles)) {
    navMain.push({ title: 'Providers', url: '/providers', icon: IconBuildingBank });
    navMain.push({ title: 'Partners', url: '/partners', icon: IconUsersGroup });
    navMain.push({ title: 'Users', url: '/users', icon: IconUsers });
    navMain.push({ title: 'File Formats', url: '/file-formats', icon: IconFileCode });
    navMain.push({ title: 'Reference Admin', url: '/reference-admin', icon: IconSettings });
    navMain.push({
      title: 'Opportunities',
      url: '/prospect-companies',
      icon: IconTarget,
      counter: opportunitiesCount,
    });
    navMain.push({ title: 'Feature Flags', url: '/feature-flags', icon: IconToggleRight });
  }

  return {
    user: {
      name: user?.name || 'Guest User',
      email: user?.email || 'guest@example.com',
      avatar: '/avatars/shadcn.jpg',
    },
    navMain,
    navClouds: [
      {
        title: 'Capture',
        icon: IconCamera,
        isActive: true,
        url: '#',
        items: [
          { title: 'Active Proposals', url: '#' },
          { title: 'Archived', url: '#' },
        ],
      },
      {
        title: 'Proposal',
        icon: IconFileDescription,
        url: '#',
        items: [
          { title: 'Active Proposals', url: '#' },
          { title: 'Archived', url: '#' },
        ],
      },
      {
        title: 'Prompts',
        icon: IconFileAi,
        url: '#',
        items: [
          { title: 'Active Proposals', url: '#' },
          { title: 'Archived', url: '#' },
        ],
      },
    ],
  };
};

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user } = useKeycloakAuth();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { count: opportunitiesCount } = useOpportunitiesCount();
  const { labels } = useSupplierDisplay();
  const { logoUrl: companyLogoUrl, isLoaded: isLogoReady } = useCompanyLogo();
  const reconciliationEnabled = useFeatureFlag('RECONCILIATION_ENABLED');
  const data = getNavigationData(
    user as { name?: string; email?: string; roles?: string[] } | null,
    {
      ...(opportunitiesCount > 0 && { opportunitiesCount }),
      suppliersLabel: labels.plural,
      reconciliationEnabled: reconciliationEnabled === true,
    }
  );

  const logoSrc =
    companyLogoUrl ||
    (isDarkMode ? '/assets/steerfi-logo-dark-mode.png' : '/assets/steerfi-logo.png');

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader className="gap-0 p-0">
        <SidebarMenu className="gap-0">
          <SidebarMenuItem className="w-full">
            <div className="flex min-h-16 shrink-0 items-center justify-center p-2 md:min-h-24">
              {isLogoReady && (
                <img
                  src={logoSrc}
                  alt={companyLogoUrl ? 'Company Logo' : 'SteerFi'}
                  className="h-10 w-auto object-contain"
                />
              )}
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <Separator className="flex-shrink-0" />
      <SidebarContent>
        <NavMain
          items={
            data.navMain as {
              title: string;
              url: string;
              icon: Icon;
              allowedTypes?: string[];
              counter?: number;
            }[]
          }
        />
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={data.user} />
      </SidebarFooter>
    </Sidebar>
  );
}
